import { useMemo, useState, useRef, useEffect } from "react";
import {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	flexRender,
	RowData,
} from "@tanstack/react-table";
import { TariffWithPricesViewType } from "../../../store/Catalogs/types";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../../components/ui/table";
import { Badge } from "../../../components/ui/badge";
import { cn } from "@/dentlab/src/lib/utils";
import { SearchInput } from "../../../components/ui/search-input.component";
import { Check, X } from "lucide-react";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../components/ui/select";
import { TariffActions } from "./tariff-actions.component";
import { EditableCell } from "./editable-cell.component";
import { PriceListDropdown } from "./price-list-dropdown.component";
import { useCatalogsStore } from "../../../store/Catalogs";
import { useCentralStore } from "../../../store/Central";
import { Skeleton } from "../../../components/ui/skeleton";
import { Input } from "../../../components/ui/input";
import { PaginationControls } from "../../../components/ui/pagination-controls.component";
import { useTemplatesStore } from "../../../store/Templates";
import { TemplateDeletionDialog } from "../../../components/template-dialogs/template-deletion-dialog.component";
import { TemplateInfoDialog } from "../../../components/template-dialogs/template-info-dialog.component";
import { TariffCodeChangeDialog } from "../../../components/template-dialogs/tariff-code-change-dialog.component";
import { useServiceCatalogStore } from "../../../store/ServiceCatalog";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

declare module "@tanstack/react-table" {
	interface TableMeta<TData extends RowData> {
		updateData: (
			rowIndex: number,
			columnId: string,
			value: unknown
		) => void;
		updatePrice: (
			rowIndex: number,
			priceListId: string,
			value: number
		) => void;
	}
}

interface TariffsTableProps {
	tariffs: TariffWithPricesViewType[];
	isLoading: boolean;
	showCatalogColumn?: boolean;
}

// Custom hook to prevent re-renders during edits
const useSkipper = () => {
	const skipRef = useRef(false);
	const shouldSkipRef = useRef(false);

	useEffect(() => {
		skipRef.current = shouldSkipRef.current;
		shouldSkipRef.current = false;
	});

	return [skipRef.current, () => (shouldSkipRef.current = true)] as const;
};

// Component for editing price cells
const PriceEditableCell: React.FC<{
	tariff: TariffWithPricesViewType;
	priceListId: string;
	priceItem: any;
	onUpdate: (value: number) => void;
}> = ({ tariff, priceListId, priceItem, onUpdate }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [value, setValue] = useState(priceItem?.price?.toString() || "");
	const inputRef = useRef<HTMLInputElement>(null);

	// Allow editing for all tariffs that are not deactivated (prices can be edited for default tariffs too)
	const isEditable = tariff.is_deactivated !== true;

	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [isEditing]);

	const handleSave = () => {
		const numValue = parseFloat(value);
		if (!isNaN(numValue) && numValue >= 0) {
			onUpdate(numValue);
		}
		setIsEditing(false);
	};

	const handleCancel = () => {
		setValue(priceItem?.price?.toString() || "");
		setIsEditing(false);
	};

	if (!isEditable) {
		return (
			<div className="text-right font-mono text-sm bg-gray-100">
				{priceItem?.price != null ? priceItem.price.toFixed(2) : "-"}
			</div>
		);
	}

	// Determine background color based on modified_by
	const bgColor = priceItem?.modified_by ? "bg-orange-50" : "bg-slate-50";

	if (isEditing) {
		return (
			<Input
				ref={inputRef}
				type="number"
				step="0.01"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				onBlur={handleSave}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						handleSave();
					} else if (e.key === "Escape") {
						handleCancel();
					}
				}}
				className="h-7 w-16 px-2 py-1 text-right font-mono text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
			/>
		);
	}

	return (
		<div
			className={cn(
				"text-right font-mono text-sm cursor-pointer px-2 py-1 -mx-2 -my-1 rounded",
				bgColor,
				"hover:opacity-80"
			)}
			onClick={() => setIsEditing(true)}
		>
			{priceItem?.price != null ? priceItem.price.toFixed(2) : "-"}
		</div>
	);
};

export const TariffsTable: React.FC<TariffsTableProps> = ({
	tariffs,
	isLoading,
	showCatalogColumn = false,
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(50);
	const {
		catalogs,
		updateCustomTariff,
		updatePriceListItem,
		visiblePriceListIds,
		togglePriceListVisibility,
		setVisiblePriceLists,
	} = useCatalogsStore();
	const { priceLists, priceListsLoading } = useCentralStore((state) => ({
		priceLists: state.priceLists,
		priceListsLoading: state.priceListsLoading,
	}));
	const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();
	const {
		reassignTemplateItems,
		removeItemFromTemplates,
		checkTemplateUsage,
	} = useTemplatesStore((state) => ({
		reassignTemplateItems: state.reassignTemplateItems,
		removeItemFromTemplates: state.removeItemFromTemplates,
		checkTemplateUsage: state.checkTemplateUsage,
	}));
	const { findMatchingDefaultTariff } = useCatalogsStore((state) => ({
		findMatchingDefaultTariff: state.findMatchingDefaultTariff,
	}));
	// Confirmation dialog state (for deactivation WITHOUT default fallback)
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
	const [confirmationTemplateIds, setConfirmationTemplateIds] = useState<
		number[]
	>([]);
	const [pendingConfirmationTariff, setPendingConfirmationTariff] = useState<{
		tariffId: string;
		codeE: string;
	} | null>(null);

	// Info dialog state (for activation, deactivation WITH default fallback, variant creation, and code_e change)
	const [infoDialogOpen, setInfoDialogOpen] = useState(false);
	const [infoTemplateIds, setInfoTemplateIds] = useState<number[]>([]);
	const [infoTariffCode, setInfoTariffCode] = useState("");
	const [infoAction, setInfoAction] = useState<
		| "activated"
		| "deactivated"
		| "variant_created"
		| "code_e_to_default"
		| "code_e_to_custom"
	>("activated");

	// Service catalog warning state
	const [catalogWarningOpen, setCatalogWarningOpen] = useState(false);
	const [catalogFolderNames, setCatalogFolderNames] = useState<string[]>([]);
	const [pendingCatalogDeactivation, setPendingCatalogDeactivation] =
		useState<{ tariffId: string; codeE: string } | null>(null);

	// Code change dialog state (for when custom tariff stops overwriting default)
	const [codeChangeDialogOpen, setCodeChangeDialogOpen] = useState(false);
	const [codeChangeInfo, setCodeChangeInfo] = useState<{
		tariffId: string;
		oldDefaultId: string;
		oldCodeE: string;
		newCodeE: string;
		affectedTemplateIds: number[];
	} | null>(null);

	const handleVariantCreated = (
		codeE: string,
		affectedTemplateIds: number[]
	) => {
		setInfoTariffCode(codeE);
		setInfoTemplateIds(affectedTemplateIds);
		setInfoAction("variant_created");
		setInfoDialogOpen(true);
	};

	const handleCodeChangeFallbackToDefault = async () => {
		if (!codeChangeInfo) return;
		await reassignTemplateItems(
			{ tariffs_custom_id: codeChangeInfo.tariffId },
			{
				tariffs_custom_id: null,
				tariffs_default_id: codeChangeInfo.oldDefaultId,
			}
		);
		setCodeChangeDialogOpen(false);
		setCodeChangeInfo(null);
	};

	const handleCodeChangeKeepCustom = () => {
		setCodeChangeDialogOpen(false);
		setCodeChangeInfo(null);
	};

	const handleCodeChangeCancel = async () => {
		if (!codeChangeInfo) return;
		await updateCustomTariff(codeChangeInfo.tariffId, {
			code_e: codeChangeInfo.oldCodeE,
		});
		setCodeChangeDialogOpen(false);
		setCodeChangeInfo(null);
	};

	const cleanupServiceCatalog = async (tariffId: string) => {
		const catalogStore = useServiceCatalogStore.getState();
		const folderNames = catalogStore.checkItemUsage({
			tariffs_custom_id: tariffId,
		});
		if (folderNames.length > 0) {
			catalogStore.removeItemGlobally({ tariffs_custom_id: tariffId });
			await catalogStore.saveCatalog();
		}
	};

	const handleDeactivateTariff = async (
		rowIndex: number,
		tariff: TariffWithPricesViewType
	) => {
		if (!tariff.id) return;
		const templateIds = await checkTemplateUsage({
			tariffs_custom_id: tariff.id,
		});

		if (templateIds.length > 0) {
			const defaultTariff = tariff.catalog_id
				? await findMatchingDefaultTariff(
						tariff.code_e || "",
						tariff.catalog_id
					)
				: null;

			if (defaultTariff) {
				await reassignTemplateItems(
					{ tariffs_custom_id: tariff.id },
					{
						tariffs_custom_id: null,
						tariffs_default_id: defaultTariff.id,
					}
				);
				await updateCustomTariff(tariff.id, { is_deactivated: true });
				await cleanupServiceCatalog(tariff.id);
				setInfoTariffCode(tariff.code_e || "");
				setInfoTemplateIds(templateIds);
				setInfoAction("deactivated");
				setInfoDialogOpen(true);
			} else {
				setPendingConfirmationTariff({
					tariffId: tariff.id,
					codeE: tariff.code_e || "",
				});
				setConfirmationTemplateIds(templateIds);
				setConfirmationDialogOpen(true);
			}
			return;
		}

		const folderNames = useServiceCatalogStore
			.getState()
			.checkItemUsage({ tariffs_custom_id: tariff.id });
		if (folderNames.length > 0) {
			setCatalogFolderNames(folderNames);
			setPendingCatalogDeactivation({
				tariffId: tariff.id,
				codeE: tariff.code_e || "",
			});
			setCatalogWarningOpen(true);
			return;
		}

		await updateCustomTariff(tariff.id, { is_deactivated: true });
	};

	const handleCatalogWarningConfirm = async () => {
		if (!pendingCatalogDeactivation) return;
		await updateCustomTariff(pendingCatalogDeactivation.tariffId, {
			is_deactivated: true,
		});
		await cleanupServiceCatalog(pendingCatalogDeactivation.tariffId);
		setCatalogWarningOpen(false);
		setPendingCatalogDeactivation(null);
		setCatalogFolderNames([]);
	};

	const handleConfirmationRemove = async () => {
		if (!pendingConfirmationTariff) return;
		await removeItemFromTemplates({
			tariffs_custom_id: pendingConfirmationTariff.tariffId,
		});
		await updateCustomTariff(pendingConfirmationTariff.tariffId, {
			is_deactivated: true,
		});
		await cleanupServiceCatalog(pendingConfirmationTariff.tariffId);
		setConfirmationDialogOpen(false);
		setPendingConfirmationTariff(null);
	};

	const handleActivateTariff = async (
		rowIndex: number,
		tariff: TariffWithPricesViewType
	) => {
		if (!tariff.id) return;

		if (tariff.catalog_id) {
			const defaultTariff = await findMatchingDefaultTariff(
				tariff.code_e || "",
				tariff.catalog_id
			);
			if (defaultTariff) {
				const templateIds = await checkTemplateUsage({
					tariffs_default_id: defaultTariff.id,
				});
				if (templateIds.length > 0) {
					await reassignTemplateItems(
						{ tariffs_default_id: defaultTariff.id },
						{
							tariffs_default_id: null,
							tariffs_custom_id: tariff.id,
						}
					);
					await updateCustomTariff(tariff.id, {
						is_deactivated: false,
					});
					setInfoTariffCode(tariff.code_e || "");
					setInfoTemplateIds(templateIds);
					setInfoAction("activated");
					setInfoDialogOpen(true);
					return;
				}
			}
		}
		await updateCustomTariff(tariff.id, { is_deactivated: false });
	};

	// Reset to first page when search term changes
	useEffect(() => {
		setPageIndex(0);
	}, [searchTerm]);

	// We need to filter based on search term
	// Aditionally, we need to sort because the operation is too expensive on the db side (for now)
	const filteredTariffs = useMemo(() => {
		let result = tariffs;

		if (searchTerm) {
			const search = searchTerm.toLowerCase();
			result = result.filter(
				(tariff) =>
					tariff.code?.toLowerCase().includes(search) ||
					tariff.code_e?.toLowerCase().includes(search) ||
					tariff.description_de?.toLowerCase().includes(search) ||
					tariff.description_fr?.toLowerCase().includes(search) ||
					tariff.description_it?.toLowerCase().includes(search)
			);
		}

		// Sort by: 1. code (ascending), 2. is_custom (false first, then true), 3. code_e (ascending)
		return [...result].sort((a, b) => {
			// First, sort by code
			const codeCompare = (a.code || "").localeCompare(b.code || "");
			if (codeCompare !== 0) return codeCompare;

			// Then, sort by is_custom (false first, true second)
			if (a.is_custom !== b.is_custom) {
				return a.is_custom ? 1 : -1;
			}

			// Finally, sort by code_e
			const codeECompare = (a.code_e || "").localeCompare(b.code_e || "");
			return codeECompare;
		});
	}, [tariffs, searchTerm]);

	// Get visible price lists
	const visiblePriceLists = useMemo(() => {
		return priceLists.filter((pl) => visiblePriceListIds.has(pl.id));
	}, [priceLists, visiblePriceListIds]);

	// Define columns
	const columns = useMemo<ColumnDef<TariffWithPricesViewType>[]>(() => {
		const catalogColumn: ColumnDef<TariffWithPricesViewType> | null =
			showCatalogColumn
				? {
						id: "catalog",
						header: "Katalog",
						size: 150,
						cell: ({ row }) => {
							const isDefault = row.original.is_custom !== true;
							const catalog = catalogs.find(
								(c) => c.id === row.original.catalog_id
							);
							return (
								<div
									className={cn(
										"text-sm",
										isDefault && "bg-gray-50 px-2 py-1"
									)}
								>
									{catalog?.name || "-"}
								</div>
							);
						},
					}
				: null;

		const baseColumns: ColumnDef<TariffWithPricesViewType>[] = [
			{
				accessorKey: "code",
				header: "Code",
				size: 100,
				cell: ({ getValue, row, column, table }) => {
					const isEditable = false; // Code column is always read-only
					const isDefault = row.original.is_custom !== true;
					return (
						<EditableCell
							getValue={getValue}
							rowIndex={row.index}
							columnId={column.id}
							isEditable={isEditable}
							onUpdate={table.options.meta?.updateData!}
							className={cn(
								"font-mono text-sm",
								isDefault && "bg-gray-50"
							)}
						/>
					);
				},
			},
			{
				accessorKey: "code_e",
				header: "Eingabe",
				size: 100,
				cell: ({ getValue, row, column, table }) => {
					const isEditable =
						row.original.is_custom === true &&
						row.original.is_deactivated !== true;
					const isDefault = row.original.is_custom !== true;
					const value = getValue() as string | null;
					const code = row.original.code;

					// Special handling for display
					if (!isEditable && value === code) {
						return (
							<span
								className={cn(
									"font-mono text-sm text-gray-500",
									isDefault && "bg-gray-50 px-2 py-1"
								)}
							>
								wie Code
							</span>
						);
					}

					return (
						<EditableCell
							getValue={getValue}
							rowIndex={row.index}
							columnId={column.id}
							isEditable={isEditable}
							onUpdate={table.options.meta?.updateData!}
							className={cn(
								"font-mono text-sm",
								isDefault && "bg-gray-50"
							)}
						/>
					);
				},
			},
			{
				accessorKey: "description_de",
				header: "Bezeichnung (DE)",
				cell: ({ getValue, row, column, table }) => {
					const isEditable =
						row.original.is_custom === true &&
						row.original.is_deactivated !== true;
					const isDefault = row.original.is_custom !== true;
					return (
						<EditableCell
							getValue={getValue}
							rowIndex={row.index}
							columnId={column.id}
							isEditable={isEditable}
							onUpdate={table.options.meta?.updateData!}
							className={cn(
								row.original.is_deactivated && "line-through",
								isDefault && "bg-gray-50"
							)}
						/>
					);
				},
			},
			{
				accessorKey: "description_fr",
				header: "Bezeichnung (FR)",
				cell: ({ getValue, row, column, table }) => {
					const isEditable =
						row.original.is_custom === true &&
						row.original.is_deactivated !== true;
					const isDefault = row.original.is_custom !== true;
					return (
						<EditableCell
							getValue={getValue}
							rowIndex={row.index}
							columnId={column.id}
							isEditable={isEditable}
							onUpdate={table.options.meta?.updateData!}
							className={cn(
								row.original.is_deactivated && "line-through",
								isDefault && "bg-gray-50"
							)}
						/>
					);
				},
			},
			{
				accessorKey: "description_it",
				header: "Bezeichnung (IT)",
				cell: ({ getValue, row, column, table }) => {
					const isEditable =
						row.original.is_custom === true &&
						row.original.is_deactivated !== true;
					const isDefault = row.original.is_custom !== true;
					return (
						<EditableCell
							getValue={getValue}
							rowIndex={row.index}
							columnId={column.id}
							isEditable={isEditable}
							onUpdate={table.options.meta?.updateData!}
							className={cn(
								row.original.is_deactivated && "line-through",
								isDefault && "bg-gray-50"
							)}
						/>
					);
				},
			},
			{
				accessorKey: "standard_quantity",
				header: "Standard Menge",
				size: 120,
				cell: ({ getValue, row, column, table }) => {
					const isEditable =
						row.original.is_custom === true &&
						row.original.is_deactivated !== true;
					const isDefault = row.original.is_custom !== true;
					return (
						<EditableCell
							getValue={getValue}
							rowIndex={row.index}
							columnId={column.id}
							isEditable={isEditable}
							onUpdate={table.options.meta?.updateData!}
							className={cn(
								"text-sm text-center",
								isDefault && "bg-gray-50"
							)}
						/>
					);
				},
			},
			{
				accessorKey: "own_tp",
				header: "Eigene TP",
				size: 100,
				cell: ({ getValue, row, column, table }) => {
					const isEditable =
						row.original.is_custom === true &&
						row.original.is_deactivated !== true;
					const isDefault = row.original.is_custom !== true;
					return (
						<EditableCell
							getValue={getValue}
							rowIndex={row.index}
							columnId={column.id}
							isEditable={isEditable}
							onUpdate={table.options.meta?.updateData!}
							className={cn(
								"text-sm text-right font-mono",
								isDefault && "bg-gray-50"
							)}
						/>
					);
				},
			},
			{
				accessorKey: "is_req_dr_tax",
				header: "Arztsteuer",
				size: 120,
				cell: ({ row, table }) => {
					const isEditable =
						row.original.is_custom === true &&
						row.original.is_deactivated !== true;
					const isDefault = row.original.is_custom !== true;
					const value = row.original.is_req_dr_tax;

					const handleToggle = () => {
						if (isEditable) {
							table.options.meta?.updateData!(
								row.index,
								"is_req_dr_tax",
								!value
							);
						}
					};

					return (
						<div
							className={cn(
								"flex justify-center",
								isDefault && "bg-gray-50 px-2 py-1",
								isEditable && "cursor-pointer hover:opacity-70"
							)}
							onClick={handleToggle}
						>
							{value === true ? (
								<Check className="h-4 w-4 text-green-600" />
							) : value === false ? (
								<X className="h-4 w-4 text-gray-400" />
							) : (
								<span className="text-gray-400">-</span>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "additional_text",
				header: "Zusatztext",
				size: 200,
				cell: ({ getValue, row, column, table }) => {
					const isEditable =
						row.original.is_custom === true &&
						row.original.is_deactivated !== true;
					const isDefault = row.original.is_custom !== true;
					return (
						<EditableCell
							getValue={getValue}
							rowIndex={row.index}
							columnId={column.id}
							isEditable={isEditable}
							onUpdate={table.options.meta?.updateData!}
							className={cn("text-sm", isDefault && "bg-gray-50")}
						/>
					);
				},
			},
			{
				accessorKey: "composition",
				header: "Zusammensetzung",
				size: 200,
				cell: ({ getValue, row, column, table }) => {
					const isEditable =
						row.original.is_custom === true &&
						row.original.is_deactivated !== true;
					const isDefault = row.original.is_custom !== true;
					return (
						<EditableCell
							getValue={getValue}
							rowIndex={row.index}
							columnId={column.id}
							isEditable={isEditable}
							onUpdate={table.options.meta?.updateData!}
							className={cn("text-sm", isDefault && "bg-gray-50")}
						/>
					);
				},
			},
			{
				id: "is_deactivated",
				header: "Status",
				size: 100,
				cell: ({ row, table }) => {
					const isEditable = row.original.is_custom === true;
					const isDeactivated = row.original.is_deactivated === true;
					const isDefault = row.original.is_custom !== true;

					if (!isEditable) {
						return null;
					}

					return (
						<Select
							value={isDeactivated ? "deactivated" : "active"}
							onValueChange={(value) => {
								if (value === "deactivated") {
									handleDeactivateTariff(
										row.index,
										row.original
									);
								} else {
									handleActivateTariff(
										row.index,
										row.original
									);
								}
							}}
						>
							<SelectTrigger className="h-8 w-[120px]">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="active">Aktiv</SelectItem>
								<SelectItem value="deactivated">
									Deaktiviert
								</SelectItem>
							</SelectContent>
						</Select>
					);
				},
			},
		];

		// Add price columns for visible price lists
		const priceColumns: ColumnDef<TariffWithPricesViewType>[] =
			visiblePriceLists.map((priceList) => ({
				id: `price_${priceList.id}`,
				header: () => (
					<div className="text-right">
						<div
							className="font-medium truncate max-w-[100px]"
							title={priceList.name}
						>
							{priceList.name}
						</div>
					</div>
				),
				size: 120,
				cell: ({ row, table }) => {
					const priceItem = row.original.price_items?.find(
						(item: any) => item.price_list_id === priceList.id
					);

					return (
						<PriceEditableCell
							tariff={row.original}
							priceListId={priceList.id}
							priceItem={priceItem}
							onUpdate={(value) =>
								table.options.meta?.updatePrice!(
									row.index,
									priceList.id,
									value
								)
							}
						/>
					);
				},
			}));

		// Timestamp columns
		const timestampColumns: ColumnDef<TariffWithPricesViewType>[] = [
			{
				accessorKey: "created_at",
				header: "Erstellt am",
				size: 140,
				cell: ({ row }) => {
					const isDefault = row.original.is_custom !== true;
					const value = row.original.created_at;
					if (!value)
						return (
							<div
								className={cn(
									"text-sm text-gray-400",
									isDefault && "bg-gray-50 px-2 py-1"
								)}
							>
								-
							</div>
						);

					const date = new Date(value);
					const formatted = date.toLocaleDateString("de-DE", {
						day: "2-digit",
						month: "2-digit",
						year: "numeric",
						hour: "2-digit",
						minute: "2-digit",
					});

					return (
						<div
							className={cn(
								"text-sm text-gray-600",
								isDefault && "bg-gray-50 px-2 py-1"
							)}
						>
							{formatted}
						</div>
					);
				},
			},
			{
				accessorKey: "modified_at",
				header: "Geändert am",
				size: 140,
				cell: ({ row }) => {
					const isDefault = row.original.is_custom !== true;
					const value = row.original.modified_at;
					if (!value)
						return (
							<div
								className={cn(
									"text-sm text-gray-400",
									isDefault && "bg-gray-50 px-2 py-1"
								)}
							>
								-
							</div>
						);

					const date = new Date(value);
					const formatted = date.toLocaleDateString("de-DE", {
						day: "2-digit",
						month: "2-digit",
						year: "numeric",
						hour: "2-digit",
						minute: "2-digit",
					});

					return (
						<div
							className={cn(
								"text-sm text-gray-600",
								isDefault && "bg-gray-50 px-2 py-1"
							)}
						>
							{formatted}
						</div>
					);
				},
			},
		];

		const finalColumns: ColumnDef<TariffWithPricesViewType>[] = [
			{
				id: "actions",
				header: "Aktionen",
				size: 100,
				cell: ({ row }) => (
					<TariffActions
						tariff={row.original}
						onVariantCreated={handleVariantCreated}
					/>
				),
			},
			{
				id: "type",
				header: "Typ",
				size: 80,
				cell: ({ row }) => {
					return row.original.is_custom === true ? (
						<Badge variant="secondary" className="text-xs">
							Eigen
						</Badge>
					) : (
						<Badge variant="outline" className="text-xs">
							Standard
						</Badge>
					);
				},
			},
			...(catalogColumn ? [catalogColumn] : []),
			...baseColumns,
			...priceColumns,
			...timestampColumns,
		];

		return finalColumns;
	}, [
		visiblePriceLists,
		priceLists,
		visiblePriceListIds,
		togglePriceListVisibility,
		showCatalogColumn,
		catalogs,
	]);

	// Create table instance
	const table = useReactTable({
		data: filteredTariffs,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		autoResetPageIndex: !autoResetPageIndex,
		state: {
			pagination: {
				pageIndex,
				pageSize,
			},
		},
		onPaginationChange: (updater) => {
			if (typeof updater === "function") {
				const newPagination = updater({ pageIndex, pageSize });
				setPageIndex(newPagination.pageIndex);
				setPageSize(newPagination.pageSize);
			}
		},
		meta: {
			updateData: async (rowIndex, columnId, value) => {
				const row = table.getRowModel().rows[rowIndex];
				const tariff = row?.original;
				if (!tariff || !tariff.is_custom || !tariff.id) return;

				skipAutoResetPageIndex();

				const result = await updateCustomTariff(tariff.id, {
					[columnId]: value,
				});

				if (!result.success) {
					console.error("Failed to update tariff");
				} else if (result.codeEChangeInfo?.wasOverwritingDefault) {
					setCodeChangeInfo({
						tariffId: tariff.id,
						oldDefaultId: result.codeEChangeInfo.oldDefaultId,
						oldCodeE: result.codeEChangeInfo.oldCodeE,
						newCodeE: result.codeEChangeInfo.newCodeE,
						affectedTemplateIds:
							result.codeEChangeInfo.affectedTemplateIds,
					});
					setCodeChangeDialogOpen(true);
				} else if (
					result.affectedTemplateIds &&
					result.affectedTemplateIds.length > 0 &&
					result.reassignDirection
				) {
					setInfoTariffCode(tariff.code_e || "");
					setInfoTemplateIds(result.affectedTemplateIds);
					setInfoAction(
						result.reassignDirection === "to_custom"
							? "code_e_to_custom"
							: "code_e_to_default"
					);
					setInfoDialogOpen(true);
				}
			},
			updatePrice: async (rowIndex, priceListId, value) => {
				// Get the actual data row from the paginated view
				const row = table.getRowModel().rows[rowIndex];
				const tariff = row?.original;
				if (!tariff || !tariff.id) return;

				const priceItem = tariff.price_items?.find(
					(item: any) => item.price_list_id === priceListId
				);

				// Skip the next auto reset
				skipAutoResetPageIndex();

				// Call store update function to create or update price
				const success = await updatePriceListItem(
					priceItem?.price_list_item_id || null,
					priceListId,
					tariff.id,
					tariff.is_custom === true,
					value
				);

				if (!success) {
					console.error("Failed to update price");
				}
			},
		},
	});

	// Loading state
	if (isLoading || priceListsLoading) {
		return (
			<div className="p-4 space-y-2">
				{[1, 2, 3, 4, 5].map((i) => (
					<Skeleton key={i} className="h-12 w-full" />
				))}
			</div>
		);
	}

	// Empty state
	if (tariffs.length === 0) {
		return (
			<div className="p-8 text-center text-gray-500 text-sm">
				Keine Leistungen verfügbar
			</div>
		);
	}

	return (
		<>
			<div className="flex flex-col h-full">
				{/* Search bar and controls */}
				<div className="p-3 border-b bg-white flex items-center gap-3">
					<div className="flex-1">
						<SearchInput
							value={searchTerm}
							onChange={setSearchTerm}
							placeholder="Suche nach Code oder Bezeichnung..."
						/>
					</div>
					<PriceListDropdown
						priceLists={priceLists}
						visiblePriceListIds={visiblePriceListIds}
						onTogglePriceList={togglePriceListVisibility}
						onSetVisiblePriceLists={setVisiblePriceLists}
					/>
				</div>

				{/* Table with filtered results */}
				<div className="flex-1 overflow-auto">
					{filteredTariffs.length === 0 ? (
						<div className="p-8 text-center text-gray-500 text-sm">
							{searchTerm
								? `Keine Leistungen für "${searchTerm}" gefunden`
								: "Keine Leistungen verfügbar"}
						</div>
					) : (
						<Table>
							<TableHeader className="sticky top-0 bg-white z-10">
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<TableHead
												key={header.id}
												style={{
													width: header.getSize(),
												}}
											>
												{header.isPlaceholder
													? null
													: flexRender(
															header.column
																.columnDef
																.header,
															header.getContext()
														)}
											</TableHead>
										))}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								{table.getRowModel().rows.map((row) => (
									<TableRow
										key={row.id}
										className={cn(
											row.original.is_custom === true
												? "bg-white hover:bg-gray-50"
												: "bg-slate-100 hover:bg-slate-150",
											row.original.is_deactivated ===
												true && "opacity-50"
										)}
									>
										{row.getVisibleCells().map((cell) => (
											<TableCell key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</TableCell>
										))}
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>

				{/* Pagination Controls */}
				<PaginationControls
					pageIndex={pageIndex}
					pageSize={pageSize}
					totalItems={filteredTariffs.length}
					pageCount={table.getPageCount()}
					canPreviousPage={table.getCanPreviousPage()}
					canNextPage={table.getCanNextPage()}
					onPageSizeChange={setPageSize}
					onPageIndexChange={setPageIndex}
					onPreviousPage={() => table.previousPage()}
					onNextPage={() => table.nextPage()}
				/>
			</div>
			<TemplateDeletionDialog
				open={confirmationDialogOpen}
				onOpenChange={setConfirmationDialogOpen}
				templateIds={confirmationTemplateIds}
				itemType="tariff"
				itemCode={pendingConfirmationTariff?.codeE || ""}
				canFallbackToDefault={false}
				onCancel={() => {
					setConfirmationDialogOpen(false);
					setPendingConfirmationTariff(null);
				}}
				onFallbackToDefault={() => {}}
				onRemoveFromTemplates={handleConfirmationRemove}
			/>
			<TemplateInfoDialog
				open={infoDialogOpen}
				onOpenChange={setInfoDialogOpen}
				templateIds={infoTemplateIds}
				tariffCode={infoTariffCode}
				action={infoAction}
			/>
			<TariffCodeChangeDialog
				open={codeChangeDialogOpen}
				onOpenChange={setCodeChangeDialogOpen}
				templateIds={codeChangeInfo?.affectedTemplateIds || []}
				oldCode={codeChangeInfo?.oldCodeE || ""}
				newCode={codeChangeInfo?.newCodeE || ""}
				onFallbackToDefault={handleCodeChangeFallbackToDefault}
				onKeepCustom={handleCodeChangeKeepCustom}
				onCancel={handleCodeChangeCancel}
			/>
			<AlertDialog
				open={catalogWarningOpen}
				onOpenChange={setCatalogWarningOpen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Tarif deaktivieren</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div>
								<p className="mb-2">
									Der Tarif &quot;
									{pendingCatalogDeactivation?.codeE}&quot;
									wird in folgenden Leistungskatalog-Ordnern
									verwendet:
								</p>
								<ul className="list-disc list-inside mb-2 max-h-40 overflow-y-auto">
									{catalogFolderNames.map((name, i) => (
										<li key={i} className="text-sm">
											{name}
										</li>
									))}
								</ul>
								<p>
									Der Tarif wird aus dem Leistungskatalog
									entfernt und deaktiviert.
								</p>
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel
							onClick={() => {
								setCatalogWarningOpen(false);
								setPendingCatalogDeactivation(null);
								setCatalogFolderNames([]);
							}}
						>
							Abbrechen
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleCatalogWarningConfirm}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							data-testid="confirm-deactivate-tariff-from-catalog"
						>
							Deaktivieren
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};
